import React from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import Transition, { removeNonStandardProps as removeTransitionProps } from './Transition';
import { resolve } from '@cvent/nucleus-dynamic-css';
import { tap, tapOrClick } from '../touchEventHandlers';
import removeKeys from '../utils/removeKeys';
import InteractiveElement from './InteractiveElement';
import { resolveTestId } from '@cvent/nucleus-test-automation';

/**
* Removes any Trigger specific properties from a props object that would be invalid
* if applied to a base DOM node. Useful when composing multiple components to prevent
* passing a component props that it doesn't use.
*/
export function removeNonStandardProps(props) {
  return removeKeys(removeTransitionProps(props), [
    'allowToggle',
    'allowMouse',
    'mouseEnterTimeout',
    'mouseLeaveTimeout',
    'allowDocumentClick',
    'onOpen',
    'onClose',
    'classes'
  ]);
}

/**
Trigger - A wrapper component to trigger the opening/closing of flyout content on click/mouseover/mouseout.
**/
export default class Trigger extends React.Component {
  static displayName = 'Trigger';
  static propTypes = {
    /** The child content for the container. */
    children: PropTypes.node.isRequired,
    /** Flag indicating whether to allow toggling tigger content via mouse click. */
    allowToggle: PropTypes.bool,
    /** Flag indicating whether to allow toggling tigger content via mouse enter/leave. */
    allowMouse: PropTypes.bool,
    /** Flag to generate an interacive trigger element with focus and keyboard support. */
    isTriggerFocusable: PropTypes.bool,
    /** Flag to apply the mouse x/y postion to the triggered child object */
    useEventPosition: PropTypes.bool,
    /** Timeout value for how long to wait after mouse enter to open the trigger content. */
    mouseEnterTimeout: PropTypes.number,
    /** Timeout value for how long to wait after mouse leave to close the trigger content. */
    mouseLeaveTimeout: PropTypes.number,
    /** Flag indicating whether to allow closing the trigger content via clicking on the document. */
    allowDocumentClick: PropTypes.bool,
    /** A callback function to execute when clicking on the trigger element. */
    onClick: PropTypes.func,
    /** A callbaack function to execute whenever the trigger content is opened. */
    onOpen: PropTypes.func,
    /** A callbaack function to execute whenever the trigger content is closed. */
    onClose: PropTypes.func,
    /** A callback function to execute when key down event happens anywehre inside the component. */
    onKeyDown: PropTypes.func,
    /** Flag to close flyout on escape key down. Defaults to true */
    closeFlyoutOnEscapeKeyDown: PropTypes.bool,
    /** Available Style Keys. */
    style: PropTypes.shape({
      /** Top level style applied to the container. */
      trigger: PropTypes.object
    })
  };
  static defaultProps = {
    allowToggle: true,
    allowMouse: true,
    useEventPosition: false,
    mouseEnterTimeout: 0,
    mouseLeaveTimeout: 300,
    allowDocumentClick: true,
    closeFlyoutOnEscapeKeyDown: true
  };

  constructor(props) {
    super(props);
    this.state = {
      showContent: false,
      shouldHideContent: true,
      mouse: {
        x: 0,
        y: 0
      }
    };
    this._mouseEnterTimer = null;
    this._mouseLeaveTimer = null;
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onMouseEnter = this.onMouseEnter.bind(this);
    this.onMouseLeave = this.onMouseLeave.bind(this);
    this.handleShow = this.handleShow.bind(this);
    this.handleHide = this.handleHide.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.documentTapHandlers = tap(this.onDocumentClick);
    this.onKeyDown = this.onKeyDown.bind(this);
  }

  componentDidMount() {
    this._isMounted = true;
    this.addClickHandler(this.props);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.allowDocumentClick !== this.props.allowDocumentClick) {
      if (nextProps.allowDocumentClick) {
        this.addClickHandler(nextProps);
      } else {
        this.removeClickHandler(nextProps);
      }
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
    if (this._mouseLeaveTimer) {
      clearTimeout(this._mouseLeaveTimer);
      this._mouseLeaveTimer = null;
    }
    if (this._mouseEnterTimer) {
      clearTimeout(this._mouseEnterTimer);
      this._mouseEnterTimer = null;
    }
    this.removeClickHandler(this.props);
  }

  /**
   * Adds a click handler to the document if allowDocumentClick is configured.
   */
  addClickHandler(props) {
    if (!props.allowDocumentClick) {
      return;
    }
    if (document && document.addEventListener) {
      // Capture events are used so the click is checked against the UI before the
      // the UI has a chance to change.
      document.addEventListener('click', this.onDocumentClick, true);
      document.addEventListener('touchstart', this.documentTapHandlers.onTouchStart, true);
      document.addEventListener('touchend', this.documentTapHandlers.onTouchEnd, true);
    }
  }
  /**
   * Removes the click handler from the document if allowDocumentClick is configured.
   */
  removeClickHandler() {
    if (!this.props.allowDocumentClick) {
      return;
    }
    if (document && document.removeEventListener) {
      document.removeEventListener('click', this.onDocumentClick, true);
      document.removeEventListener('touchstart', this.documentTapHandlers.onTouchStart, true);
      document.removeEventListener('touchend', this.documentTapHandlers.onTouchEnd, true);
    }
  }

  /**
   * Handles hiding the content if allowDocumentClick is configured and the user clicks
   * anywhere outside of the trigger or its content.
   */
  onDocumentClick(event) {
    if (!this.props.allowDocumentClick || !this._isMounted) {
      return;
    }
    const component = ReactDOM.findDOMNode(this);
    const target = event.target ? event.target : window.event.srcElement;
    if (!component.contains(target)) {
      this.handleHide();
    }
  }

  onMouseEnter(event) {
    event.persist();
    if (!this.props.allowMouse) {
      return;
    }

    if (this.props.mouseEnterTimeout <= 0) {
      this.handleShow(event);
      return;
    }

    // Set flag indicating that the content should be hidden.
    this.setState({
      shouldShowContent: true
    });

    // If a mouse leave timer has already been set from a previous mouse leave, clear it.
    if (this._mouseEnterTimer) {
      clearTimeout(this._mouseEnterTimer);
    }

    // Set a new mouse leave timer to hide the content after the specified time as long
    // as the flag indicating that we should hide the content is still set.
    this._mouseEnterTimer = setTimeout(() => {
      if (this.state.shouldShowContent) {
        this.handleShow(event);
      }
    }, this.props.mouseEnterTimeout);
  }

  onMouseLeave() {
    if (!this.props.allowMouse) {
      return;
    }

    if (this.props.mouseLeaveTimeout <= 0) {
      this.handleHide();
      return;
    }

    // Set flag indicating that the content should be hidden.
    this.setState({
      shouldHideContent: true
    });

    // If a mouse leave timer has already been set from a previous mouse leave, clear it.
    if (this._mouseLeaveTimer) {
      clearTimeout(this._mouseLeaveTimer);
    }

    // Set a new mouse leave timer to hide the content after the specified time as long
    // as the flag indicating that we should hide the content is still set.
    this._mouseLeaveTimer = setTimeout(() => {
      if (this.state.shouldHideContent) {
        this.handleHide();
      }
    }, this.props.mouseLeaveTimeout);
  }

  onKeyDown(e) {
    const { isTriggerFocusable, onKeyDown, closeFlyoutOnEscapeKeyDown } = this.props;
    if (onKeyDown) onKeyDown(e);
    // Handle escape key
    if (e.keyCode === 27 && closeFlyoutOnEscapeKeyDown) {
      this.handleHide();
      if (isTriggerFocusable && this.trigger) {
        this.trigger.focus();
      }
    }
  }
  /**
   * Sets state flags to show the content.
   */
  handleShow(event) {
    if (!this.state.showContent && this.props.onOpen) {
      this.props.onOpen();
    }
    let x = 0;
    let y = 0;
    if (event) {
      const elementRect = event.target.getBoundingClientRect();
      x = elementRect.left;
      y = elementRect.top;
    }
    this.setState({
      showContent: true,
      shouldShowContent: true,
      shouldHideContent: false,
      mouse: {
        x,
        y
      }
    });
  }

  /**
   * Sets state flags to hide the content.
   */
  handleHide() {
    if (this.state.showContent && this.props.onClose) {
      this.props.onClose();
    }
    this.setState({
      showContent: false,
      shouldShowContent: false,
      shouldHideContent: true
    });
  }

  /**
   * Toggles the state of the content between showing and hiding if allowToggle is configured.
   */
  handleClick(event) {
    if (this.props.onClick) {
      this.props.onClick(event);
    }
    if (!this.props.allowToggle) {
      return;
    }
    event.stopPropagation();
    if (this.state.showContent) {
      this.handleHide();
    } else {
      this.handleShow(event);
    }
  }

  render() {
    const { useEventPosition, isTriggerFocusable } = this.props;
    // Triggers are not valid unless there are exactly two children.
    if (!this.props.children || React.Children.count(this.props.children) !== 2) {
      throw new Error('Exactly two children were not supplied to the Trigger!');
    }

    // Populate content if needed.
    let content = [];
    let positionStyle = {};
    if (useEventPosition) {
      positionStyle = {
        position: 'absolute',
        top: `${this.state.mouse.y}px`,
        left: `${this.state.mouse.x}px`
      };
    }
    if (this.state.showContent) {
      content = [(
        <span key="content" style={useEventPosition ? positionStyle : null}
          onMouseEnter={this.props.allowMouse ? this.handleShow : null}
          onMouseLeave={this.onMouseLeave}>
          {this.props.children[1]}
        </span>
      )];
    }

    const TriggerElement = isTriggerFocusable ? InteractiveElement : 'span';
    const conditionalProps = isTriggerFocusable ? { ref: c => this.trigger = c } : undefined;
    return (
      <div
        {...resolveTestId(this.props)}
        {...resolve(this.props, 'trigger')}
        onKeyDown={this.onKeyDown}
      >
        <TriggerElement
          {...tapOrClick(this.handleClick)}
          onMouseEnter={this.onMouseEnter}
          onMouseLeave={this.onMouseLeave}
          {...conditionalProps}
        >
          {this.props.children[0]}
        </TriggerElement>
        <Transition {...this.props} defaultName={this.constructor.displayName}>
          {content}
        </Transition>
      </div>
    );
  }
}
